import React, { useRef, useState } from 'react';
import { Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function proxyUrl(url) {
  if (!url) return '';
  return `${API}/proxy-image?url=${encodeURIComponent(url)}`;
}

export function ShareReading({ cards, readingData, type = 'reading' }) {
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const cardNames = cards?.map(c => c.name).join(' | ') || '';
  const quote = type === 'reading'
    ? readingData?.reading?.affirmation || 'Trust the wisdom within you.'
    : readingData?.message || 'Let today\'s energy guide your path.';
  const cardName = type === 'daily' ? cards?.[0]?.name || 'Your Card' : '';

  const shareText = type === 'reading'
    ? `My Tarot Reading: ${cardNames}\n\n"${quote}"\n\nDiscover your own path at tarotroom.app`
    : `Today's Energy Card: ${cardName}\n\n"${quote}"\n\nFind your daily guidance at tarotroom.app`;

  const generateImage = async () => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#070A1A',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      return canvas;
    } catch {
      return null;
    }
  };

  const handleShare = async () => {
    setSharing(true);
    // Try native share with image first
    if (navigator.share) {
      const canvas = await generateImage();
      if (canvas) {
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], 'tarot-reading.png', { type: 'image/png' });
            try {
              await navigator.share({ title: type === 'reading' ? 'My Tarot Reading' : 'Energy of the Day', text: shareText, files: [file] });
            } catch (e) { console.log('Share cancelled or failed:', e.name); }
          }
          setSharing(false);
        });
      } else {
        try { await navigator.share({ title: type === 'reading' ? 'My Tarot Reading' : 'Energy of the Day', text: shareText }); } catch (e) { console.log('Share cancelled:', e.name); }
        setSharing(false);
      }
    } else {
      // Fallback: download image
      const canvas = await generateImage();
      if (canvas) {
        const link = document.createElement('a');
        link.download = `tarot-room-${type}-${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
      setSharing(false);
    }
  };

  return (
    <div data-testid="share-reading-section">
      {/* Hidden shareable card image with actual tarot card images */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={cardRef} style={{ width: 600, padding: 40, background: '#070A1A', fontFamily: 'Playfair Display, Georgia, serif' }}>
          <div style={{ border: '1px solid rgba(212,175,55,0.3)', borderRadius: 16, padding: 32, background: 'linear-gradient(135deg, #0F0B1E 0%, #1D1129 50%, #0F0B1E 100%)' }}>
            <p style={{ color: '#D4AF37', fontSize: 12, letterSpacing: 4, textTransform: 'uppercase', textAlign: 'center', marginBottom: 20 }}>
              {type === 'reading' ? 'TAROT ROOM READING' : 'ENERGY OF THE DAY'}
            </p>
            {type === 'reading' ? (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                {cards?.slice(0, 5).map((card, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    {card.image_url ? (
                      <img
                        src={proxyUrl(card.image_url)}
                        alt={card.name}
                        style={{ width: 80, height: 120, borderRadius: 6, border: '1px solid rgba(212,175,55,0.4)', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ width: 80, height: 120, borderRadius: 6, border: '1px solid rgba(212,175,55,0.4)', background: 'rgba(212,175,55,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 24, color: '#D4AF37' }}>{card.arcana === 'major' ? '\u2726' : '\u2727'}</span>
                      </div>
                    )}
                    <p style={{ color: '#FFFFF0', fontSize: 9, marginTop: 4, maxWidth: 80 }}>{card.name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                {cards?.[0]?.image_url ? (
                  <img
                    src={proxyUrl(cards[0].image_url)}
                    alt={cardName}
                    style={{ width: 100, height: 150, borderRadius: 8, border: '2px solid rgba(212,175,55,0.4)', objectFit: 'cover', margin: '0 auto', display: 'block' }}
                  />
                ) : (
                  <div style={{ width: 100, height: 150, borderRadius: 8, border: '2px solid rgba(212,175,55,0.4)', background: 'rgba(212,175,55,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                    <span style={{ fontSize: 36, color: '#D4AF37' }}>{cards?.[0]?.arcana === 'major' ? '\u2726' : '\u2727'}</span>
                  </div>
                )}
                <p style={{ color: '#FFFFF0', fontSize: 14, marginTop: 8 }}>{cardName}</p>
              </div>
            )}
            <div style={{ borderTop: '1px solid rgba(212,175,55,0.2)', paddingTop: 16, marginTop: 8 }}>
              <p style={{ color: '#FFFFF0', fontSize: 16, fontStyle: 'italic', textAlign: 'center', lineHeight: 1.6 }}>
                "{quote}"
              </p>
            </div>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <p style={{ color: '#D4AF37', fontSize: 18, fontWeight: 'bold' }}>TAROT ROOM</p>
              <p style={{ color: '#BDB2C4', fontSize: 10, marginTop: 2 }}>by The Soul Balm | tarotroom.app</p>
            </div>
          </div>
        </div>
      </div>

      {/* Single clean share button */}
      <button
        onClick={handleShare}
        disabled={sharing}
        data-testid="share-reading-btn"
        className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-[#D4AF37]/30 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-all duration-300 disabled:opacity-50 mx-auto"
      >
        <Share2 size={16} />
        {sharing ? 'Preparing...' : 'Share Your Reading'}
      </button>
    </div>
  );
}
